import { useEffect, useState } from 'react';
import {
  getCompanionMemories,
  getCompanionContacts,
  createCompanionMemory,
  updateCompanionMemory,
  deleteCompanionMemory,
  createCompanionContact,
  updateCompanionContact,
  deleteCompanionContact,
} from '../api';
import { CompanionMemory, CompanionContact } from '../types';

interface CompanionMemoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  worldId: string;
}

const MEMORY_TYPES: CompanionMemory['type'][] = [
  'preference',
  'biographical',
  'event',
  'goal',
  'relationship',
  'date',
  'misc',
];

export function CompanionMemoryModal({ isOpen, onClose, worldId }: CompanionMemoryModalProps) {
  const [memories, setMemories] = useState<CompanionMemory[]>([]);
  const [contacts, setContacts] = useState<CompanionContact[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'memories' | 'people'>('memories');
  const [contactEditingId, setContactEditingId] = useState<string | null>(null);
  const [newContactName, setNewContactName] = useState('');
  const [newContactRelation, setNewContactRelation] = useState('');
  const [newContactNotes, setNewContactNotes] = useState('');
  const [editContactName, setEditContactName] = useState('');
  const [editContactRelation, setEditContactRelation] = useState('');
  const [editContactNotes, setEditContactNotes] = useState('');

  const [newType, setNewType] = useState<CompanionMemory['type']>('preference');
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newConfidence, setNewConfidence] = useState('0.7');

  const [editType, setEditType] = useState<CompanionMemory['type']>('preference');
  const [editKey, setEditKey] = useState('');
  const [editValue, setEditValue] = useState('');
  const [editConfidence, setEditConfidence] = useState('0.7');

  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    setIsLoadingContacts(true);
    setError(null);
    getCompanionMemories(worldId)
      .then((res) => setMemories(res.memories))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load memories'))
      .finally(() => setIsLoading(false));
    getCompanionContacts(worldId)
      .then((res) => setContacts(res.contacts))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load contacts'))
      .finally(() => setIsLoadingContacts(false));
  }, [isOpen, worldId]);

  const handleCreate = async () => {
    if (!newKey.trim() || !newValue.trim()) {
      setError('Key and value are required.');
      return;
    }
    setError(null);
    const result = await createCompanionMemory(worldId, {
      type: newType,
      key: newKey.trim(),
      value: newValue.trim(),
      confidence: Number(newConfidence) || 0.7,
    });
    setMemories((prev) => [result.memory, ...prev]);
    setNewKey('');
    setNewValue('');
  };

  const startEdit = (memory: CompanionMemory) => {
    setEditingId(memory.id);
    setEditType(memory.type);
    setEditKey(memory.key);
    setEditValue(memory.value);
    setEditConfidence(String(memory.confidence ?? 0.7));
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setError(null);
    const result = await updateCompanionMemory(worldId, editingId, {
      type: editType,
      key: editKey.trim(),
      value: editValue.trim(),
      confidence: Number(editConfidence) || 0.7,
    });
    setMemories((prev) => prev.map(m => (m.id === editingId ? (result.memory ?? m) : m)));
    setEditingId(null);
  };

  const handleDelete = async (memoryId: string) => {
    await deleteCompanionMemory(worldId, memoryId);
    setMemories((prev) => prev.filter(m => m.id !== memoryId));
  };

  const handleCreateContact = async () => {
    if (!newContactName.trim()) {
      setError('Contact name is required.');
      return;
    }
    setError(null);
    const result = await createCompanionContact(worldId, {
      name: newContactName.trim(),
      relation: newContactRelation.trim() || null,
      notes: newContactNotes.trim() || null,
    });
    setContacts((prev) => [result.contact, ...prev]);
    setNewContactName('');
    setNewContactRelation('');
    setNewContactNotes('');
  };

  const startContactEdit = (contact: CompanionContact) => {
    setContactEditingId(contact.id);
    setEditContactName(contact.name);
    setEditContactRelation(contact.relation ?? '');
    setEditContactNotes(contact.notes ?? '');
  };

  const cancelContactEdit = () => {
    setContactEditingId(null);
  };

  const saveContactEdit = async () => {
    if (!contactEditingId) return;
    if (!editContactName.trim()) {
      setError('Contact name is required.');
      return;
    }
    setError(null);
    const result = await updateCompanionContact(worldId, contactEditingId, {
      name: editContactName.trim(),
      relation: editContactRelation.trim() || null,
      notes: editContactNotes.trim() || null,
    });
    setContacts((prev) => prev.map(c => (c.id === contactEditingId ? (result.contact ?? c) : c)));
    setContactEditingId(null);
  };

  const handleDeleteContact = async (contactId: string) => {
    await deleteCompanionContact(worldId, contactId);
    setContacts((prev) => prev.filter(c => c.id !== contactId));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-dark-800 rounded-xl shadow-2xl w-full max-w-3xl mx-4 border border-dark-600 max-h-[85vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-600">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-dark-50">Companion Memory</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActiveTab('memories')}
                className={`px-3 py-1 text-xs rounded-full border ${
                  activeTab === 'memories'
                    ? 'bg-primary-600 text-white border-primary-500'
                    : 'bg-dark-700 text-dark-300 border-dark-600'
                }`}
              >
                Memories
              </button>
              <button
                onClick={() => setActiveTab('people')}
                className={`px-3 py-1 text-xs rounded-full border ${
                  activeTab === 'people'
                    ? 'bg-primary-600 text-white border-primary-500'
                    : 'bg-dark-700 text-dark-300 border-dark-600'
                }`}
              >
                People
              </button>
            </div>
          </div>
          <button onClick={onClose} className="text-dark-400 hover:text-dark-200 transition-colors">✕</button>
        </div>

        <div className="px-6 py-4 space-y-6 overflow-y-auto max-h-[70vh]">
          {error && <div className="text-red-400 text-sm">{error}</div>}

          {activeTab === 'memories' && (
            <>
              <div className="bg-dark-700 rounded-lg p-4 border border-dark-600">
                <h3 className="text-sm font-semibold text-dark-100 mb-3">Add Memory</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <select
                    value={newType}
                    onChange={(e) => setNewType(e.target.value as CompanionMemory['type'])}
                    className="w-full px-3 py-2 rounded bg-dark-700 border border-dark-500 text-dark-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {MEMORY_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  <input
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    placeholder="Key"
                    className="w-full px-3 py-2 rounded bg-dark-700 border border-dark-500 text-dark-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    placeholder="Value"
                    className="w-full px-3 py-2 rounded bg-dark-700 border border-dark-500 text-dark-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    value={newConfidence}
                    onChange={(e) => setNewConfidence(e.target.value)}
                    placeholder="Confidence"
                    className="w-full px-3 py-2 rounded bg-dark-700 border border-dark-500 text-dark-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <button onClick={handleCreate} className="btn-primary mt-3">Add Memory</button>
              </div>

              <div className="space-y-3">
                {isLoading && <div className="text-dark-300 text-sm">Loading memories...</div>}
                {!isLoading && memories.length === 0 && (
                  <div className="text-dark-400 text-sm">No memories yet.</div>
                )}
                {memories.map((memory) => (
                  <div key={memory.id} className="bg-dark-700 rounded-lg p-4 border border-dark-600">
                    {editingId === memory.id ? (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <select
                          value={editType}
                          onChange={(e) => setEditType(e.target.value as CompanionMemory['type'])}
                          className="w-full px-3 py-2 rounded bg-dark-700 border border-dark-500 text-dark-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          {MEMORY_TYPES.map((type) => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                        <input
                          value={editKey}
                          onChange={(e) => setEditKey(e.target.value)}
                          className="w-full px-3 py-2 rounded bg-dark-700 border border-dark-500 text-dark-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <input
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-full px-3 py-2 rounded bg-dark-700 border border-dark-500 text-dark-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <input
                          value={editConfidence}
                          onChange={(e) => setEditConfidence(e.target.value)}
                          className="w-full px-3 py-2 rounded bg-dark-700 border border-dark-500 text-dark-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <div className="flex gap-2">
                          <button onClick={saveEdit} className="btn-primary">Save</button>
                          <button onClick={cancelEdit} className="btn-secondary">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                          <div className="text-xs text-dark-400">{memory.type}</div>
                          <div className="text-sm font-semibold text-dark-100">{memory.key}</div>
                          <div className="text-sm text-dark-200">{memory.value}</div>
                          <div className="text-xs text-dark-500">Confidence: {memory.confidence ?? 0.7}</div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => startEdit(memory)} className="btn-secondary">Edit</button>
                          <button onClick={() => handleDelete(memory.id)} className="btn-secondary text-red-400">Delete</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === 'people' && (
            <div className="space-y-4">
              <div className="bg-dark-700 rounded-lg p-4 border border-dark-600">
                <h3 className="text-sm font-semibold text-dark-100 mb-3">Add Person</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    value={newContactName}
                    onChange={(e) => setNewContactName(e.target.value)}
                    placeholder="Name"
                    className="w-full px-3 py-2 rounded bg-dark-700 border border-dark-500 text-dark-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    value={newContactRelation}
                    onChange={(e) => setNewContactRelation(e.target.value)}
                    placeholder="Relation"
                    className="w-full px-3 py-2 rounded bg-dark-700 border border-dark-500 text-dark-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    value={newContactNotes}
                    onChange={(e) => setNewContactNotes(e.target.value)}
                    placeholder="Notes"
                    className="w-full px-3 py-2 rounded bg-dark-700 border border-dark-500 text-dark-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <button onClick={handleCreateContact} className="btn-primary mt-3">Add Person</button>
              </div>

              <div className="space-y-3">
              {isLoadingContacts && <div className="text-dark-300 text-sm">Loading contacts...</div>}
              {!isLoadingContacts && contacts.length === 0 && (
                <div className="text-dark-400 text-sm">No people recorded yet.</div>
              )}
              {contacts.map((contact) => (
                <div key={contact.id} className="bg-dark-700 rounded-lg p-4 border border-dark-600">
                  {contactEditingId === contact.id ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <input
                        value={editContactName}
                        onChange={(e) => setEditContactName(e.target.value)}
                        className="w-full px-3 py-2 rounded bg-dark-700 border border-dark-500 text-dark-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <input
                        value={editContactRelation}
                        onChange={(e) => setEditContactRelation(e.target.value)}
                        className="w-full px-3 py-2 rounded bg-dark-700 border border-dark-500 text-dark-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <input
                        value={editContactNotes}
                        onChange={(e) => setEditContactNotes(e.target.value)}
                        className="w-full px-3 py-2 rounded bg-dark-700 border border-dark-500 text-dark-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <div className="flex gap-2">
                        <button onClick={saveContactEdit} className="btn-primary">Save</button>
                        <button onClick={cancelContactEdit} className="btn-secondary">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-dark-100">{contact.name}</div>
                        <div className="text-xs text-dark-400">{contact.relation ?? 'Unknown relation'}</div>
                        {contact.notes && (
                          <div className="text-xs text-dark-300 mt-1">{contact.notes}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-xs text-dark-500">
                          Mentions: {contact.mention_count ?? 0}
                        </div>
                        <button onClick={() => startContactEdit(contact)} className="btn-secondary">Edit</button>
                        <button onClick={() => handleDeleteContact(contact.id)} className="btn-secondary text-red-400">Delete</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
